<template>
  <aside class="app-sidebar" :class="{ collapsed: isCollapsed }">
    <div class="sidebar-brand">
      <div
        class="brand-icon-wrap"
        :class="{ clickable: isCollapsed }"
        @click="expandIfCollapsed"
      >
        <img :src="logoSrc" alt="Orderly" class="brand-icon" />
      </div>
      <div class="brand-copy">
        <h1>Orderly</h1>
        <p>{{ packageName }}</p>
      </div>
      <button
        class="collapse-toggle"
        type="button"
        @click="toggleSidebar"
        :title="isCollapsed ? 'Expandir menu' : 'Colapsar menu'"
        :aria-label="isCollapsed ? 'Expandir menu' : 'Colapsar menu'"
      >
        <i class="fa" :class="isCollapsed ? 'fa-angle-right' : 'fa-angle-left'" aria-hidden="true"></i>
      </button>
    </div>

    <nav class="sidebar-nav">
      <div
        v-for="section in menuSections"
        :key="section.key"
        class="menu-section"
      >
        <a
          v-if="isSingleLinkSection(section)"
          :href="resolveNavHref(resolveSectionDirectUrl(section))"
          class="section-trigger section-trigger--link"
          :class="{ active: isSectionActive(section) }"
          @click="handleNavClick($event, resolveSectionDirectUrl(section))"
        >
          <div class="section-main">
            <i :class="section.icon" aria-hidden="true"></i>
            <span class="menu-label">{{ section.title }}</span>
          </div>
        </a>

        <button
          v-else
          class="section-trigger"
          type="button"
          :class="{ active: isSectionActive(section) }"
          @click="toggleSection(section.key)"
        >
          <div class="section-main">
            <i :class="section.icon" aria-hidden="true"></i>
            <span class="menu-label">{{ section.title }}</span>
          </div>
          <i
            class="fa section-caret"
            :class="isSectionOpen(section.key) ? 'fa-angle-down' : 'fa-angle-right'"
            aria-hidden="true"
          ></i>
        </button>

        <div v-if="!isSingleLinkSection(section) && !isCollapsed && isSectionOpen(section.key)" class="submenu-wrap">
          <template v-for="item in section.submenu" :key="item.key">
            <button
              v-if="item.type === 'dropdown'"
              class="submenu-trigger"
              type="button"
              :class="{ active: isDropdownActive(section.key, item) }"
              @click="toggleDropdown(section.key, item)"
            >
              <div class="submenu-main">
                <i :class="item.icon" aria-hidden="true"></i>
                <span>{{ item.title }}</span>
              </div>
              <i
                class="fa"
                :class="isDropdownOpen(section.key, item.key) ? 'fa-angle-down' : 'fa-angle-right'"
                aria-hidden="true"
              ></i>
            </button>

            <div
              v-if="item.type === 'dropdown' && isDropdownOpen(section.key, item.key)"
              class="submenu-options"
            >
              <a
                v-for="option in item.options"
                :key="option.key"
                :href="resolveNavHref(option.url)"
                class="submenu-link nested"
                :class="{ active: isUrlActive(option.url, true) }"
                @click="handleNavClick($event, option.url)"
              >
                <i :class="option.icon" aria-hidden="true"></i>
                <span>{{ option.title }}</span>
              </a>
            </div>

            <a
              v-if="item.type !== 'dropdown'"
              :href="resolveNavHref(item.url)"
              class="submenu-link"
              :class="{ active: isUrlActive(item.url) }"
              @click="handleNavClick($event, item.url)"
            >
              <i :class="item.icon" aria-hidden="true"></i>
              <span>{{ item.title }}</span>
            </a>
          </template>
        </div>
      </div>
    </nav>

    <div class="sidebar-profile">
      <button
        ref="profileAvatarButton"
        type="button"
        class="avatar sidebar-profile-avatar-btn clickable"
        title="Abrir menú de perfil"
        @click="handleAvatarClick"
      >
        {{ userInitials }}
      </button>
      <div
        class="profile-copy profile-copy--clickable"
        title="Abrir menú de perfil"
        @click="toggleProfileMenu"
      >
        <span>{{ userName }}</span>
        <small>{{ userRole }}</small>
      </div>

      <div v-if="!isCollapsed" class="sidebar-profile-actions">
        <button
          type="button"
          class="sidebar-profile-icon-btn"
          title="Cerrar sesión"
          aria-label="Cerrar sesión"
          @click="openLogoutConfirm"
        >
          <i class="fa fa-sign-out" aria-hidden="true"></i>
        </button>

        <button
          ref="profileMenuToggleButton"
          type="button"
          class="sidebar-profile-icon-btn"
          :title="isProfileMenuOpen ? 'Ocultar opciones de perfil' : 'Mostrar opciones de perfil'"
          :aria-label="isProfileMenuOpen ? 'Ocultar opciones de perfil' : 'Mostrar opciones de perfil'"
          @click="toggleProfileMenu"
        >
          <i class="fa" :class="isProfileMenuOpen ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="isProfileMenuOpen"
        class="sidebar-profile-dropdown"
        :style="profileMenuStyle"
      >
        <button type="button" class="sidebar-profile-dropdown__item" @click="goToMyProfile">
          <i class="fa fa-user-circle sidebar-profile-dropdown__icon" aria-hidden="true"></i>
          <span>Mi perfil</span>
        </button>
        <button type="button" class="sidebar-profile-dropdown__item" @click="notifyRemindersInProgress">
          <i class="fa fa-bell sidebar-profile-dropdown__icon" aria-hidden="true"></i>
          <span>Recordatorios</span>
        </button>
        <button
          type="button"
          class="sidebar-profile-dropdown__item sidebar-profile-dropdown__item--danger"
          @click="openLogoutConfirm"
        >
          <i class="fa fa-sign-out sidebar-profile-dropdown__icon" aria-hidden="true"></i>
          <span>Cerrar sesión</span>
        </button>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showLogoutConfirm"
        class="sidebar-logout-modal-backdrop"
        @click.self="closeLogoutConfirm"
      >
        <div class="sidebar-logout-modal" role="dialog" aria-modal="true" aria-labelledby="sidebar-logout-title">
          <h4 id="sidebar-logout-title">Cerrar sesión</h4>
          <p>¿Estás seguro de que deseas cerrar sesión?</p>

          <div class="sidebar-logout-modal__actions">
            <button type="button" class="sidebar-logout-modal__btn sidebar-logout-modal__btn--cancel" @click="closeLogoutConfirm">
              Cancelar
            </button>
            <button type="button" class="sidebar-logout-modal__btn sidebar-logout-modal__btn--confirm" @click="confirmLogout">
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </aside>
</template>

<script>
import logoSrc from '@/assets/images/logo/minilogo.png';
import accountService from '@/services/account';
import menuService from '@/services/menu';
import axios from 'axios';
import { clearCapabilitiesSnapshot } from '@/utils/capabilities';
import {
  filterSidebarSectionsForScopedAccessPatch,
  isScopedAccessPatchUser,
} from '@/utils/scopedAccessPatch';

export default {
  name: 'AppSidebar',
  data() {
    return {
      isCollapsed: false,
      logoSrc,
      packageName: 'Sin paquete',
      userName: 'Usuario',
      userRole: 'Sin rol',
      userInitials: 'US',
      menuSections: [],
      openSections: {},
      openDropdowns: {},
      isProfileMenuOpen: false,
      floatingProfileMenuTop: 0,
      floatingProfileMenuLeft: 0,
      showLogoutConfirm: false,
    };
  },
  watch: {
    '$route.fullPath'() {
      this.syncActiveOpenState();
    }
  },
  mounted() {
    this.isCollapsed = localStorage.getItem('orderly.sidebar.collapsed') === '1';
    this.loadMenu();
    this.loadSidebarContext();
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeProfileMenu);
    window.addEventListener('scroll', this.closeProfileMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeProfileMenu);
    window.removeEventListener('scroll', this.closeProfileMenu, true);
  },
  computed: {
    profileMenuStyle() {
      return {
        top: `${this.floatingProfileMenuTop}px`,
        left: `${this.floatingProfileMenuLeft}px`,
      };
    },
  },
  methods: {
    isSingleLinkSection(section) {
      const submenu = Array.isArray(section?.submenu) ? section.submenu : [];
      if (submenu.length !== 1) {
        return false;
      }

      const first = submenu[0] || {};
      return String(first?.type || 'link') === 'link';
    },
    resolveSectionDirectUrl(section) {
      const submenu = Array.isArray(section?.submenu) ? section.submenu : [];
      if (submenu.length === 0) {
        return String(section?.url || '').trim();
      }

      return String(submenu[0]?.url || section?.url || '').trim();
    },
    async loadMenu() {
      try {
        const response = await menuService.getSidebar();
        const sections = response?.data?.sections || response?.menu || [];
        const normalizedSections = Array.isArray(sections) ? sections : [];

        this.menuSections = isScopedAccessPatchUser()
          ? filterSidebarSectionsForScopedAccessPatch(normalizedSections)
          : normalizedSections;
      } catch (error) {
        this.menuSections = [];
      } finally {
        this.seedOpenState();
        this.syncActiveOpenState();
      }
    },
    async loadSidebarContext() {
      const fallbackUser = this.getStoredUser();
      this.applyUserProfile(this.resolveUserName(fallbackUser), fallbackUser?.role || '');

      try {
        const response = await accountService.getDetails();
        const data = response?.data || {};

        const packageName = String(data?.package?.name || '').trim();
        if (packageName) {
          this.packageName = packageName;
        }

        const apiUser = data?.user || {};
        const finalName = String(apiUser?.name || this.resolveUserName(fallbackUser) || '').trim();
        const finalRole = String(apiUser?.role || fallbackUser?.role || '').trim();

        this.applyUserProfile(finalName, finalRole);
      } catch (error) {
      }
    },
    resolveUserName(user) {
      const firstName = String(user?.first_name || '').trim();
      const lastName = String(user?.last_name || '').trim();
      const fullName = `${firstName} ${lastName}`.trim();

      if (fullName) {
        return fullName;
      }

      return String(user?.name || '').trim();
    },
    getStoredUser() {
      const rawUser = localStorage.getItem('user');
      if (!rawUser || rawUser === 'undefined') {
        return {};
      }

      try {
        return JSON.parse(rawUser) || {};
      } catch (error) {
        return {};
      }
    },
    applyUserProfile(name, role) {
      const safeName = String(name || '').trim();
      const safeRole = String(role || '').trim();

      this.userName = safeName || 'Usuario';
      this.userRole = safeRole || 'Sin rol';
      this.userInitials = this.buildInitials(this.userName);
    },
    buildInitials(fullName) {
      const parts = String(fullName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (parts.length === 0) {
        return 'US';
      }

      const first = parts[0]?.charAt(0) || '';
      const second = (parts[1] || parts[0])?.charAt(0) || '';
      const initials = `${first}${second}`.toUpperCase();

      return initials || 'US';
    },
    seedOpenState() {
      const nextOpenSections = {};

      this.menuSections.forEach((section, index) => {
        nextOpenSections[section.key] = index === 0;
      });

      this.openSections = nextOpenSections;
    },
    syncActiveOpenState() {
      this.menuSections.forEach((section) => {
        if (this.isSectionActive(section)) {
          this.openSections[section.key] = true;
        }

        (section.submenu || []).forEach((item) => {
          if (item.type === 'dropdown' && Array.isArray(item.options)) {
            const hasActiveOption = item.options.some((option) => this.isUrlActive(option.url));
            if (hasActiveOption) {
              this.openSections[section.key] = true;
              this.setDropdownState(section.key, item.key, true);
            }
          }
        });
      });
    },
    setDropdownState(sectionKey, itemKey, open) {
      const key = `${sectionKey}::${itemKey}`;
      this.openDropdowns = {
        ...this.openDropdowns,
        [key]: open,
      };
    },
    toggleSection(sectionKey) {
      if (this.isCollapsed) {
        this.expandIfCollapsed();
        this.openSections[sectionKey] = true;
        return;
      }

      this.openSections = {
        ...this.openSections,
        [sectionKey]: !this.openSections[sectionKey],
      };
    },
    toggleDropdown(sectionKey, item) {
      const itemKey = String(item?.key || '').trim();
      if (!itemKey) {
        return;
      }

      const key = `${sectionKey}::${itemKey}`;
      const nextValue = !this.openDropdowns[key];

      this.openDropdowns = {
        ...this.openDropdowns,
        [key]: nextValue,
      };
    },
    isSectionOpen(sectionKey) {
      return !!this.openSections[sectionKey];
    },
    isDropdownOpen(sectionKey, itemKey) {
      return !!this.openDropdowns[`${sectionKey}::${itemKey}`];
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem('orderly.sidebar.collapsed', this.isCollapsed ? '1' : '0');
      this.closeProfileMenu();
    },
    expandIfCollapsed() {
      if (!this.isCollapsed) return;
      this.isCollapsed = false;
      localStorage.setItem('orderly.sidebar.collapsed', '0');
    },
    isSectionActive(section) {
      return (section.submenu || []).some((item) => {
        if (item.type === 'dropdown' && Array.isArray(item.options)) {
          return item.options.some((option) => this.isUrlActive(option.url, true));
        }

        return this.isUrlActive(item.url);
      });
    },
    isDropdownActive(sectionKey, item) {
      if (!Array.isArray(item.options)) {
        return false;
      }

      return item.options.some((option) => this.isUrlActive(option.url, true));
    },
    normalizePath(url) {
      const raw = String(url || '').trim();
      if (!raw) return '';

      const noHash = raw.split('#')[0];
      const noQuery = noHash.split('?')[0];
      const normalized = noQuery.replace(/\/+$/, '');

      return normalized || '/';
    },
    isUrlActive(url, exact = false) {
      const target = this.normalizePath(url);
      const current = this.normalizePath(this.$route.path);

      if (!target || target === '#') {
        return false;
      }

      if (target === '/') {
        return current === '/';
      }

      if (exact) {
        return current === target;
      }

      return current === target || current.startsWith(`${target}/`);
    },
    navigateTo(url) {
      const target = String(url || '').trim();
      if (!target) return;

      if (target.includes('#') || /^https?:\/\//i.test(target)) {
        window.location.href = target;
        return;
      }

      this.$router.push(target).catch(() => {});
    },
    resolveNavHref(url) {
      const target = String(url || '').trim();
      if (!target) {
        return '#';
      }

      if (target.includes('#') || /^https?:\/\//i.test(target)) {
        return target;
      }

      const routeLocation = this.$router.resolve(target);
      return routeLocation?.href || target;
    },
    handleNavClick(event, url) {
      // Preserve browser-native behavior for middle/right click and modified clicks.
      if (event && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      this.navigateTo(url);
    },
    handleAvatarClick(event) {
      this.toggleProfileMenu(event);
    },
    toggleProfileMenu(event) {
      const nextValue = !this.isProfileMenuOpen;
      this.isProfileMenuOpen = nextValue;

      if (!nextValue) {
        return;
      }

      const trigger = this.resolveProfileMenuTriggerElement(event);
      if (!trigger) {
        return;
      }

      this.computeProfileMenuPosition(trigger);
    },
    resolveProfileMenuTriggerElement(event) {
      const explicitTarget = event?.currentTarget;
      if (explicitTarget instanceof Element) {
        return explicitTarget;
      }

      const collapseTrigger = this.$refs.profileAvatarButton;
      if (this.isCollapsed && collapseTrigger instanceof Element) {
        return collapseTrigger;
      }

      const openTrigger = this.$refs.profileMenuToggleButton;
      if (openTrigger instanceof Element) {
        return openTrigger;
      }

      return null;
    },
    computeProfileMenuPosition(triggerElement) {
      const rect = triggerElement.getBoundingClientRect();
      const menuWidth = 210;
      const menuHeight = 130;
      const viewportPadding = 8;
      const gap = 6;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const maxLeft = Math.max(viewportPadding, viewportWidth - menuWidth - viewportPadding);
      const preferredLeft = this.isCollapsed ? rect.right + gap : rect.left;
      const left = Math.min(Math.max(viewportPadding, preferredLeft), maxLeft);

      const canOpenDown = rect.bottom + gap + menuHeight <= viewportHeight - viewportPadding;
      const top = canOpenDown
        ? rect.bottom + gap
        : Math.max(viewportPadding, rect.top - menuHeight - gap);

      this.floatingProfileMenuTop = top;
      this.floatingProfileMenuLeft = left;
    },
    closeProfileMenu() {
      this.isProfileMenuOpen = false;
    },
    handleGlobalClick(event) {
      if (!this.isProfileMenuOpen && !this.showLogoutConfirm) {
        return;
      }

      const target = event?.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest('.sidebar-profile-dropdown') || target.closest('.sidebar-profile')) {
        return;
      }

      this.closeProfileMenu();
    },
    goToMyProfile() {
      this.closeProfileMenu();
      this.$router.push({
        name: 'settingsUsersRoles',
        query: {
          tab: 'profile',
        },
      }).catch(() => {});
    },
    notifyRemindersInProgress() {
      this.closeProfileMenu();
      const toast = this.$toast;
      if (toast && typeof toast.info === 'function') {
        toast.info('Recordatorios aun esta trabajandose.');
      }
    },
    openLogoutConfirm() {
      this.closeProfileMenu();
      this.showLogoutConfirm = true;
    },
    closeLogoutConfirm() {
      this.showLogoutConfirm = false;
    },
    confirmLogout() {
      this.showLogoutConfirm = false;
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      clearCapabilitiesSnapshot();
      axios.defaults.headers.common.Authorization = '';

      this.$router.replace({ name: 'login' }).catch(() => {});
    },
  },
};
</script>
